import { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { BibleGeneralAIAssistant } from '../components/BibleGeneralAIAssistant';
import React from 'react';
import { HelpButton } from '../components/common/HelpButton';
import { ProFeatureGuard } from '../components/auth/ProFeatureGuard';

export default function AIInsightsPage() {
  return (
    <ProFeatureGuard feature="AI Insights">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">AI Insights</h1>
            <HelpButton pageName="ai-insights" className="ml-2" />
          </div>
        </div>
        <div className="min-h-screen p-4 sm:p-6 md:p-8">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Lightbulb className="w-8 h-8 text-gold-500" />
              <h1 className="text-2xl sm:text-3xl font-bold text-white">AI Assistant</h1>
            </div>
            <p className="text-white/60">
              Discover deeper understanding of scripture through AI-powered analysis.
            </p>
          </header>

          <div className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-4 sm:p-6">
            <BibleGeneralAIAssistant />
          </div>
        </div>
      </div>
    </ProFeatureGuard>
  );
}

function BiblicalThemes() {
  const themes = [
    // Core Spiritual Themes
    {
      title: 'Love and Relationships',
      description: 'Biblical guidance on love, marriage, and relationships.',
      verses: ['1 Corinthians 13:4-7', 'Ephesians 5:25-33', 'Song of Solomon 8:6-7', 'Genesis 2:24'],
      color: 'from-rose-500 to-pink-500'
    },
    {
      title: 'Faith and Trust',
      description: 'Building and maintaining faith in challenging times.',
      verses: ['Hebrews 11:1', 'Romans 10:17', 'Mark 11:22-24', 'Proverbs 3:5-6'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Heaven and Afterlife',
      description: 'Biblical teachings about heaven, eternal life, and resurrection.',
      verses: ['John 14:2-3', 'Revelation 21:4', '1 Corinthians 15:42-44', 'Philippians 3:20-21'],
      color: 'from-indigo-500 to-purple-500'
    },
    {
      title: 'Peace and Comfort',
      description: 'Finding peace and comfort in God during difficult times.',
      verses: ['Philippians 4:6-7', 'John 14:27', 'Psalm 23:4', 'Isaiah 41:10'],
      color: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Purpose and Calling',
      description: 'Understanding God\'s purpose and calling for your life.',
      verses: ['Jeremiah 29:11', 'Romans 8:28', 'Ephesians 2:10', 'Philippians 1:6'],
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {themes.map((theme, index) => (
        <ThemeCard key={index} theme={theme} />
      ))}
    </div>
  );
}

function ThemeCard({ theme }: { theme: { title: string; description: string; verses: string[]; color: string } }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${theme.color} p-[1px]`}>
      <div className="relative bg-obsidian-900/95 rounded-xl p-4">
        <h3 className="text-xl font-semibold text-white mb-2">{theme.title}</h3>
        <p className="text-white/70 text-sm mb-4">{theme.description}</p>
        
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-sm font-medium text-white/90 hover:text-white flex items-center gap-1"
        >
          <Book size={16} />
          <span>{isOpen ? 'Hide Verses' : 'View Verses'}</span>
        </button>

        {isOpen && (
          <div className="mt-4 space-y-2">
            {theme.verses.map((verse, index) => (
              <div key={index} className="text-sm text-white/80 hover:text-white">
                {verse}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
